/* eslint-disable react-hooks/exhaustive-deps */
import { Path, Signal, StoreKey } from "@common/constans";
import { MenuCode, SearchMode, StorageType } from "@common/enum";
import { mapCmsAirportToAirport, mapCmsCountryToCountry, mapCmsRouteToRoute } from "@common/models";
import { IBanner, IMenuItem } from "@common/models/CMS";
import TopSideBanner from "@components/banner/TopSideBanner";
import Footer from "@components/Footer";
import Preloader from "@components/general/Preloader";
import Toasts from "@components/general/Toasts";
import Header from "@components/header/Header";
import { bookingUrls, mobileViewPage, planePage, scrollDisableURLs, scrollUpExceptionURLs, transactionTimeoutUrls } from "@config/route";
import { Culture, ILocale } from "@core/index";
import { AppAction, IAppAction } from "@misc/Action";
import { store } from "@misc/Reducer";
import { IAppState } from "@misc/State";
import { PageMeta } from "@pages/page";
import Abort from "@service/Abort";
import { Account } from "@service/Account";
import Config from "@service/Config";
import { apolloQuery, GET_ROUTE, IMsterData, IRouteData, MASTER_DATA } from "@service/graphql";
import Notification from "@service/Notification";
import { Action, authenticate, member } from "@service/proxy";
import Splash from "@service/Splash";
import Storage from "@service/Storage";
import { clearStoreForSearch, getWidth } from "@utilize/Util";
import { FC, Fragment, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import BookingTransactionTimeout from "./BookingTransactionTimeout";
import TransactionTimeout from "./TransactionTimeout";

interface IRoot {
    menuItems: IMenuItem[];
}

let timer: NodeJS.Timeout;
export const Root: FC<IRoot> = ({ menuItems, children }) => {
    const dispatch = useDispatch();
    const { push } = useHistory();
    const { pathname, search } = useLocation();
    const { code } = Culture.getLocale();
    const [top, setTop] = useState<number>(0);
    const query = new URLSearchParams(search);
    const { user, banners, preloader, newRoute, headerStyle, footerStyle } = useSelector((state: IAppState) => ({
        user: state.user,
        banners: state.banners,
        preloader: state.preloader,
        newRoute: state.newRoute,
        headerStyle: state.headerStyle,
        footerStyle: state.footerMode
    }));

    useEffect(() => {
        window.addEventListener("resize", onResize);

        apolloQuery<IRouteData>(GET_ROUTE)
            .then((response) => {
                const airports = response.data.airport?.slice().sort((a, b) => (a?.sortIndex || 0) - (b?.sortIndex || 0))?.map(x => mapCmsAirportToAirport(x, response.data.englishAirport)) || [];
                const routes = response.data.route?.map(x => mapCmsRouteToRoute(x)).filter(x => x.availableForIB) || [];
                dispatch<IAppAction>({ type: AppAction.Airports, payload: { airports } });
                dispatch<IAppAction>({ type: AppAction.Routes, payload: { routes } });
            })
            .catch(() => {
                dispatch<IAppAction>({ type: AppAction.ValidationErrors, payload: { validationErrors: [{ origin: "notFound" }] } });
            });

        apolloQuery<IMsterData>(MASTER_DATA)
            .then((response) => {
                //set a copy of response.data.airport for use sort on this data. <[...response.data.airport!]>
                const excludedBanners = Storage.get<IBanner[]>(StoreKey.ExcludedBanners, StorageType.Session) || [];
                const countriesData = response.data.country?.slice().sort((a, b) => { return ((a.name || "") < (b.name || "")) ? -1 : 1 })?.slice().sort((a, b) => (a?.sortIndex || 0) - (b?.sortIndex || 0))?.map(x => mapCmsCountryToCountry(x, response.data.englishCountry)).filter(country => country.flagPosition);
                dispatch<IAppAction>({ type: AppAction.CountriesData, payload: { countriesData } });
                dispatch<IAppAction>({ type: AppAction.Fleets, payload: { fleets: response.data.fleet } });
                dispatch<IAppAction>({ type: AppAction.Genders, payload: { genders: response.data.gender } });
                dispatch<IAppAction>({ type: AppAction.Banners, payload: { banners: response.data.banner?.filter(x => !excludedBanners.find(b => b.contentItemId === x.contentItemId)) } });
                dispatch<IAppAction>({ type: AppAction.CmsAncillaries, payload: { cmsAncillaries: response.data.ancillary } })
            });
        const cultures = Culture.getCultures();
        const languages: ILocale[] = [];
        for (const key in cultures) {
            if (Object.prototype.hasOwnProperty.call(cultures, key)) {
                const language = cultures[key];
                languages.push(language.locale);
                dispatch<IAppAction>({ type: AppAction.Languages, payload: { languages } });
            }
        }
        // get code param from url and authenticate user
        const authCode = query.get("code");
        const pass = query.get("pass");
        const action = query.get("action");
        if (authCode && action === null) {
            const redirectUri = Config.get("redirectUrl");
            store.dispatch<IAppAction>({ type: AppAction.ShowHeaderSkeleton, payload: { showHeaderSkeleton: true } });
            authenticate(Action.POST, { data: { code: authCode, redirectUri } })
                .then((response) => {
                    const authenticate = response as any;
                    Account.setToken(authenticate.access_token);
                    Account.setRefreshToken(authenticate.refresh_token);
                    Account.setIdToken(authenticate.id_token);
                    return member(Action.GET);
                })
                .then((response) => {
                    Account.login(response.content);
                })
                .catch(() => {
                    Account.logout();
                })
                .finally(() => {
                    store.dispatch<IAppAction>({ type: AppAction.ShowHeaderSkeleton, payload: { showHeaderSkeleton: false } });
                });
        } else if (pass) {
            Account.logout();
            push(Path.Home);
        }

        if (pathname !== Path.Home) {
            Splash.hide();
        }

        if (pathname === Path.Signout) {
            push(Path.Home);
            Account.logout();
        } else if (!planePage.includes(pathname) && user.memberId) {
            // get member api call
            store.dispatch<IAppAction>({ type: AppAction.ShowHeaderSkeleton, payload: { showHeaderSkeleton: true } });
            member(Action.GET)
                .then((response) => {
                    Account.login(response.content);
                })
                .catch(() => {
                    Account.logout();
                })
                .finally(() => {
                    store.dispatch<IAppAction>({ type: AppAction.ShowHeaderSkeleton, payload: { showHeaderSkeleton: false } });
                })
        }

        // add mobile view css
        if (mobileViewPage.includes(pathname))
            document.documentElement.classList.add("mobile-view");
        else
            document.documentElement.classList.remove("mobile-view");
        // init default locale in redux
        dispatch<IAppAction>({ type: AppAction.Locale, payload: { locale: code } });
    }, []);

    useEffect(() => {
        if (!transactionTimeoutUrls.some(t => pathname.includes(t))) {
            dispatch<IAppAction>({ type: AppAction.TransactionTimeout, payload: { transactionTimeout: { start: false } } });
        }

        if (!bookingUrls.some((b) => pathname.includes(b))) {
            Abort.get(Signal.Booking)?.abort();
            dispatch<IAppAction>({ type: AppAction.BookingTransactionTimeout, payload: { bookingTransactionTimeout: { start: false } } });
        }

        if (scrollDisableURLs.some(x => pathname.includes(x))) {
            document.documentElement.style.setProperty('overflow-y', "hidden");
        } else {
            document.documentElement.style.removeProperty('overflow-y');
        }

        if (pathname === Path.Home && Storage.get(StoreKey.ChangePassword)) {
            Notification.success("change_password_successfully");
            Storage.remove(StoreKey.ChangePassword);
        }

        const lastRoute = Storage.get<string>(StoreKey.LastRoute, StorageType.Session);
        if (lastRoute) {
            push(lastRoute);
            Storage.remove(StoreKey.LastRoute, StorageType.Session);
            Splash.hide();
        } else if (pathname === Path.Home) {
            clearStoreForSearch(SearchMode.Create, true);
        }
    }, [pathname]);

    useEffect(() => {
        const bannerRef = document.getElementById('topSideBanner');
        if (pathname === Path.Home && banners.filter(x => x.type === "topside") && bannerRef)
            setTop(bannerRef.getBoundingClientRect().height);
        else
            setTop(0)
    }, [banners, pathname]);

    useEffect(() => {
        if (newRoute) {
            push(newRoute);
            dispatch<IAppAction>({ type: AppAction.NewRoute, payload: { newRoute: "" } });
        }
    }, [newRoute])

    const onResize = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            const windowWidth = getWidth();
            dispatch<IAppAction>({ type: AppAction.SetWidth, payload: { windowWidth } })
        }, 200);
    }

    return (
        <Fragment>
            {
                pathname === Path.Home && banners.filter(x => x.type === "topside").length ? <TopSideBanner dataSource={banners.filter(x => x.type === "topside")} /> : ""
            }
            {
                !planePage.some(p => pathname.startsWith(p)) &&
                <Header
                    menuItem={menuItems?.find(m => m.code?.code === MenuCode[MenuCode.PublicMenu]) || {}}
                    scrollUpExceptionURLs={scrollUpExceptionURLs}
                />
            }
            <main
                id="contentWrapper"
                className={`content-wrapper${planePage.find(p => pathname.startsWith(p)) || headerStyle.menuStyle === "Transparent" ? "" : " padding-top"}`}
                style={{ top: `${top || 0}px` }}
            >
                <section className="flex-grow">
                    <Suspense fallback={<Preloader />}>
                        <PageMeta>
                            {children}
                        </PageMeta>
                    </Suspense>
                </section>
                {
                    !planePage.includes(pathname) && footerStyle !== "None" &&
                    <Footer
                        menuItem={menuItems?.find(m => m.code?.code === MenuCode[MenuCode.PublicFooter]) ?? {}}
                        socialNetworks={menuItems?.find(m => m.code?.code === MenuCode[MenuCode.SocialNetworks])}
                    />
                }
            </main>
            {
                preloader.show &&
                <Preloader
                    message={preloader.message}
                    mode={preloader.mode}
                    title={preloader.title}
                />
            }
            <BookingTransactionTimeout />
            <TransactionTimeout />
            <Toasts />
        </Fragment>
    );
}